import React, { useCallback, useEffect, useRef, useState } from 'react';
import {
  applyDrag,
  fitBounds,
  lockedAxisDelta,
  SNAP_ENTER_PX,
  SNAP_RELEASE_PX,
  snapBox,
  snapTargets,
  type StickyState,
} from './cropAdjustSnap';

export interface Box {
  left: number;
  top: number;
  width: number;
  height: number;
}

export type BoxHandle = 'move' | 'nw' | 'ne' | 'sw' | 'se';

/** Active snap-guide line positions (source px), or null when not snapped on that axis. */
export interface SnapGuides {
  x: number | null;
  y: number | null;
}

const NO_GUIDES: SnapGuides = { x: null, y: null };
type Bounds = { width: number; height: number };

/** The centred sub-rectangle of `box` with the given width:height `ratio` (absolute coords). */
export function inscribeRatio(box: Box, ratio: number): Box {
  const wide = box.width / box.height > ratio;
  const width = wide ? box.height * ratio : box.width;
  const height = wide ? box.height : box.width / ratio;
  return {
    left: box.left + (box.width - width) / 2,
    top: box.top + (box.height - height) / 2,
    width,
    height,
  };
}

/**
 * Draggable / resizable boxes over a source image. `getScale()` returns the
 * current rendered-px ÷ source-px ratio so pointer deltas map back to source
 * coordinates. `begin(i, handle)` is a pointer-down handler factory.
 *
 * While dragging: holding **Shift** restricts a move to one axis — whichever
 * of horizontal/vertical the drag first commits to — and keeps it locked to
 * that axis for the rest of the drag, so you can deliberately move a box
 * straight across or straight down without drift on the other axis (same
 * convention as PowerPoint/Illustrator/Figma). Edges/centers snap to the
 * canvas and to other boxes by default, with hysteresis — once snapped, the
 * box stays on that line until dragged clearly away, rather than needing the
 * cursor to sit inside a razor-thin window; holding **Ctrl/Cmd** temporarily
 * turns snapping off entirely for fine positioning (also the common
 * convention — Figma and Sketch use the same held-key-disables-snap model).
 * Snapping is skipped during a ratio-locked resize, where the shape is
 * already fully determined by the lock.
 */
export function useDragBoxes(
  initial: Box[],
  bounds: Bounds,
  getScale: () => number,
  getRatio?: () => number | undefined,
) {
  const [boxes, setBoxes] = useState<Box[]>(initial);
  const [guides, setGuides] = useState<SnapGuides>(NO_GUIDES);

  const begin = (index: number, handle: BoxHandle) => (event: React.PointerEvent) => {
    event.preventDefault();
    event.stopPropagation();
    const startX = event.clientX;
    const startY = event.clientY;
    const scale = getScale() || 1;
    const start: Box = boxes[index];
    const others = boxes.filter((_, i) => i !== index);
    let sticky: StickyState = { x: null, y: null };
    let lockedAxis: 'x' | 'y' | null = null;

    const onMove = (ev: PointerEvent) => {
      let dx = (ev.clientX - startX) / scale;
      let dy = (ev.clientY - startY) / scale;
      if (handle === 'move' && ev.shiftKey) {
        const locked = lockedAxisDelta(dx, dy, SNAP_ENTER_PX / scale, lockedAxis);
        dx = locked.dx;
        dy = locked.dy;
        lockedAxis = locked.axis;
      } else {
        lockedAxis = null;
      }
      const ratio = getRatio?.();
      let next = applyDrag(start, handle, dx, dy, bounds, ratio);
      let nextGuides = NO_GUIDES;
      const snapDisabled = ev.ctrlKey || ev.metaKey || (!!ratio && handle !== 'move');
      if (!snapDisabled) {
        const snapped = snapBox(
          next,
          handle,
          snapTargets(others, bounds),
          SNAP_ENTER_PX / scale,
          SNAP_RELEASE_PX / scale,
          sticky,
        );
        next = fitBounds(snapped.box, bounds);
        nextGuides = snapped.guides;
        sticky = snapped.sticky;
      } else {
        sticky = { x: null, y: null };
      }
      setGuides(nextGuides);
      setBoxes((prev) => prev.map((b, i) => (i === index ? next : b)));
    };
    const onUp = () => {
      window.removeEventListener('pointermove', onMove);
      window.removeEventListener('pointerup', onUp);
      setGuides(NO_GUIDES);
    };
    window.addEventListener('pointermove', onMove);
    window.addEventListener('pointerup', onUp);
  };

  return { boxes, begin, setBoxes, guides };
}

/**
 * Bundles the render-scale tracking (source-px ÷ rendered-px) with the draggable
 * boxes so `CropAdjustDialog` stays presentational. `measure` is wired to the
 * source image's `onLoad` and to window resizes.
 */
export function useCropCanvas(
  source: { url: string; width: number; height: number } | null,
  initial: Box[],
  ratio?: number,
) {
  const wrapRef = useRef<HTMLDivElement>(null);
  const [scale, setScale] = useState(1);

  const measure = useCallback(() => {
    if (wrapRef.current && source) setScale(wrapRef.current.clientWidth / source.width);
  }, [source]);

  useEffect(() => {
    measure();
    window.addEventListener('resize', measure);
    return () => window.removeEventListener('resize', measure);
  }, [measure]);

  const bounds = source ?? { width: 1, height: 1 };
  const { boxes, begin, setBoxes, guides } = useDragBoxes(
    initial,
    bounds,
    () => scale,
    () => ratio,
  );

  // Turning the lock on snaps every box to the largest ratio-shaped rect it
  // already contains, so nothing outside the box is ever pulled in.
  useEffect(() => {
    if (ratio) setBoxes((prev) => prev.map((b) => inscribeRatio(b, ratio)));
  }, [ratio, setBoxes]);

  // Snapshot of the boxes as they were when this adjust session opened —
  // `initial` is only consulted by useDragBoxes' useState on first render, so
  // a later re-render with a new array reference (the caller re-rendering)
  // must not change what "reset" reverts to; the ref freezes it.
  const openedWithRef = useRef(initial);
  const resetBoxes = useCallback(() => {
    setBoxes(
      ratio ? openedWithRef.current.map((b) => inscribeRatio(b, ratio)) : openedWithRef.current,
    );
  }, [ratio, setBoxes]);

  return { wrapRef, scale, boxes, begin, measure, guides, resetBoxes };
}

/** Absolute offsets for a corner resize handle (12px square, centred on the corner). */
export const cornerOffset: Record<Exclude<BoxHandle, 'move'>, React.CSSProperties> = {
  nw: { top: -6, left: -6, cursor: 'nwse-resize' },
  ne: { top: -6, right: -6, cursor: 'nesw-resize' },
  sw: { bottom: -6, left: -6, cursor: 'nesw-resize' },
  se: { bottom: -6, right: -6, cursor: 'nwse-resize' },
};
